import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import type { PopoverRootActions } from '@base-ui/react/popover'
import * as Sentry from '@sentry/nextjs'
import { Loader, Popover } from '@systemeio/components'
import { FaceSmileIcon } from '@systemeio/icons'
import { cn } from 'cn'
import type { Emoji } from 'emoji-mart'
import dynamic from 'next/dynamic'
import { ToolbarButton } from 'modules/comments/components/comment-editor/common/toolbar-button'

const EmojiPickerLoader = (): ReactNode => (
  <div className="flex size-64 items-center justify-center rounded-lg bg-bg-primary shadow-2">
    <Loader />
  </div>
)

const Picker = dynamic(async () => import('@emoji-mart/react'), {
  loading: () => <EmojiPickerLoader />,
})

interface EmojiPickerProps {
  onEmojiSelect: (emoji: typeof Emoji.Props) => void
  iconClassName?: string
  pickerClassName?: string
}

export const EmojiPicker = ({
  onEmojiSelect,
  pickerClassName = '',
  iconClassName = '',
}: EmojiPickerProps): ReactNode => {
  const actionsRef = useRef<PopoverRootActions | null>(null)
  // TODO: Define proper type for emojiData
  const [emojiData, setEmojiData] = useState<unknown>(null)

  const loadEmojiPicker = async (): Promise<void> => {
    if (emojiData) return

    try {
      const response = await fetch('https://cdn.jsdelivr.net/npm/@emoji-mart/data', {
        headers: {
          Accept: 'application/json',
        },
      })
      setEmojiData(await response.json())
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'Failed to load emoji picker' },
      })
    }
  }

  return (
    <Popover.Root actionsRef={actionsRef}>
      <Popover.Trigger render={<ToolbarButton type="button" className="hidden md:block" />}>
        <FaceSmileIcon
          className={cn(
            'size-5',
            `cursor-pointer text-text-secondary hover:text-text-primary`,
            iconClassName,
          )}
          onMouseEnter={loadEmojiPicker}
        />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Positioner
          side="top"
          sideOffset={6}
          collisionAvoidance={{ fallbackAxisSide: 'none' }}
        >
          <Popover.Popup className={cn(pickerClassName, 'z-50')}>
            {emojiData ? (
              <Picker
                data={emojiData}
                onEmojiSelect={(emoji: typeof Emoji.Props) => {
                  onEmojiSelect(emoji)
                  actionsRef.current?.close()
                }}
              />
            ) : (
              <EmojiPickerLoader />
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
